import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation } from 'react-router-dom'

import { MarketingMobileNav } from '@/components/marketing/marketing-mobile-nav'
import { PodiumWordmark } from '@/components/brand/podium-wordmark'
import {
  NavMobileCloseButton,
  NavMobileMenuButton,
} from '@/components/ui/nav-mobile-close-button'
import { featureLinks, getAppUrl, headerNavLinks } from '@/config/marketing-nav.config'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu'
import { Button } from '@/components/ui/button'
import { useMarketingScrollChrome } from '@/hooks/use-marketing-scroll-chrome'
import { cn } from '@/lib/utils'
import { MarketingScrollFab } from '@/components/marketing/marketing-scroll-fab'

function isFeatureLinkActive(pathname: string, href: string): boolean {
  return href === '/' ? pathname === '/' : pathname.startsWith(href)
}

export function MarketingHeader() {
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const loginHref = getAppUrl('/login')
  const signupHref = getAppUrl('/signup')

  const isFeaturesActive = featureLinks.some((link) =>
    isFeatureLinkActive(location.pathname, link.href)
  )

  const closeMobile = () => setMobileOpen(false)
  const { showScrollFab } = useMarketingScrollChrome(!mobileOpen)
  const sheetRef = useRef<HTMLDivElement>(null)

  // Mobile sheet a11y: lock scroll, make the rest of the page inert (traps
  // focus in the portaled sheet + hides it from AT), Escape to close, move
  // focus into the sheet, and restore focus to the trigger on close.
  useEffect(() => {
    if (!mobileOpen) return
    const root = document.getElementById('marketing-root')
    const restoreTo = document.activeElement as HTMLElement | null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    root?.setAttribute('inert', '')

    sheetRef.current
      ?.querySelector<HTMLElement>(
        'a[href], button, input, [tabindex]:not([tabindex="-1"])'
      )
      ?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setMobileOpen(false)
      }
    }
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
      root?.removeAttribute('inert')
      restoreTo?.focus?.()
    }
  }, [mobileOpen])

  return (
    <>
      <header className="marketing-header">
        <div className="nav-shell">
          <div
            className={cn(
              'nav-bar gap-3',
              'md:grid md:grid-cols-[auto_1fr_auto] md:gap-4'
            )}
          >
            <Link to="/" className="shrink-0 justify-self-start" aria-label="Podium Markets home">
              <PodiumWordmark variant="dark" height={32} className="h-[var(--nav-logo-height)] w-auto" aria-hidden />
            </Link>

            <NavigationMenu className="mx-auto hidden md:flex" aria-label="Main">
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger
                    className={cn(isFeaturesActive && 'nav-link--active')}
                  >
                    Features
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="nav-menu-panel">
                    <ul className="nav-menu-list">
                      {featureLinks.map((link) => {
                        const active = isFeatureLinkActive(location.pathname, link.href)
                        return (
                          <li key={link.href}>
                            <NavigationMenuLink asChild>
                              <Link
                                to={link.href}
                                className={cn(
                                  'nav-dropdown-link',
                                  active && 'nav-dropdown-link--active'
                                )}
                              >
                                {link.label}
                              </Link>
                            </NavigationMenuLink>
                          </li>
                        )
                      })}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {headerNavLinks.map((link) => (
                  <NavigationMenuItem key={link.href}>
                    <NavigationMenuLink asChild>
                      <Link
                        to={link.href}
                        className={cn(
                          navigationMenuTriggerStyle(),
                          'nav-link',
                          location.pathname === link.href && 'nav-link--active'
                        )}
                      >
                        {link.label}
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                ))}
              </NavigationMenuList>
            </NavigationMenu>

            <div className="ml-auto flex items-center gap-2 justify-self-end md:ml-0">
              <a href={loginHref} className="nav-login hidden sm:inline-flex">
                Log In
              </a>
              <Button
                asChild
                variant="cta"
                size="cta"
                className="hidden sm:inline-flex"
              >
                <a href={signupHref} className="nav-cta">
                  Get Started
                </a>
              </Button>
              <NavMobileMenuButton
                className="md:hidden"
                aria-expanded={mobileOpen}
                aria-controls="marketing-mobile-nav"
                aria-label="Open menu"
                onClick={() => setMobileOpen(true)}
              />
            </div>
          </div>
        </div>
      </header>
      <div className="marketing-header-spacer" aria-hidden />

      {mobileOpen && typeof document !== 'undefined'
        ? createPortal(
            <div ref={sheetRef} className="nav-mobile-overlay md:hidden" role="presentation">
              <div className="nav-mobile-sheet">
                <div className="nav-mobile-sheet__header">
                  <Link to="/" onClick={closeMobile} aria-label="Podium Markets home">
                    <PodiumWordmark variant="dark" height={32} className="h-[var(--nav-logo-height)] w-auto" aria-hidden />
                  </Link>
                  <NavMobileCloseButton aria-label="Close menu" onClick={closeMobile} />
                </div>

                <MarketingMobileNav
                  loginHref={loginHref}
                  signupHref={signupHref}
                  onNavigate={closeMobile}
                />
              </div>
            </div>,
            document.body
          )
        : null}

      <MarketingScrollFab visible={showScrollFab && !mobileOpen} />
    </>
  )
}
